import { createFileRoute } from '@tanstack/react-router';
import { CalendarClock, PanelRightOpen, ShieldCheck } from 'lucide-react';

import { Badge } from '@apps/web/components/ui/badge';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@apps/web/components/ui/card';

const REVIEW_VISIT_KEY = 'v=16:s=3:d=2099-03-26';

export const Route = createFileRoute(
    '/_app/(main)/extension-review/calendar/php/unassigned',
)({
    component: ExtensionReviewPage,
    head: () => ({
        meta: [{ title: 'Extension Review | Phoebe' }],
    }),
});

function ExtensionReviewPage() {
    return (
        <div className="mx-auto flex w-full max-w-5xl flex-col gap-6 p-6">
            <section className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="secondary">Authenticated test page</Badge>
                    <Badge variant="outline">AxisCare-style open visit</Badge>
                </div>
                <div className="space-y-2">
                    <h1 className="text-2xl font-semibold tracking-normal">
                        Phoebe Extension Review
                    </h1>
                    <p className="text-muted-foreground max-w-2xl text-sm">
                        This hidden page gives store reviewers a safe,
                        authenticated surface where the Phoebe browser extension
                        can open its side panel and attach its staffing action
                        to a simulated open visit.
                    </p>
                </div>
            </section>
            <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <CalendarClock className="size-4" />
                            Open Visits
                        </CardTitle>
                        <CardDescription>
                            Simulated scheduler view for extension review.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-hidden rounded-sm border">
                            <div className="bg-muted text-muted-foreground grid grid-cols-[120px_1fr_120px] gap-3 border-b px-4 py-2 text-xs font-medium tracking-normal uppercase">
                                <span>Date</span>
                                <span>Visit</span>
                                <span>Status</span>
                            </div>
                            <div
                                data-visitkey={REVIEW_VISIT_KEY}
                                className="grid grid-cols-[120px_1fr_120px] items-start gap-3 px-4 py-4 text-sm"
                            >
                                <div className="font-medium">Mar 26, 2099</div>
                                <div className="space-y-1">
                                    <div className="font-semibold">
                                        Mrs. Review Client
                                    </div>
                                    <div className="text-muted-foreground">
                                        9:00 AM to 1:00 PM
                                    </div>
                                    <div className="text-muted-foreground">
                                        Homemaker visit
                                    </div>
                                </div>
                                <div className="font-medium text-amber-700">
                                    Unassigned
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-base">
                            <PanelRightOpen className="size-4" />
                            Review Checklist
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 text-sm">
                        <div className="flex gap-3">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-green-700" />
                            <div>
                                <div className="font-medium">
                                    Requires Phoebe sign-in
                                </div>
                                <p className="text-muted-foreground">
                                    Unauthenticated visitors are redirected to
                                    sign in by the normal app route guard.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-green-700" />
                            <div>
                                <div className="font-medium">
                                    Hidden from navigation
                                </div>
                                <p className="text-muted-foreground">
                                    The route is reachable only by direct URL.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <ShieldCheck className="mt-0.5 size-4 shrink-0 text-green-700" />
                            <div>
                                <div className="font-medium">
                                    Extension-enabled
                                </div>
                                <p className="text-muted-foreground">
                                    The published extension allowlist covers
                                    this page without enabling every Phoebe app
                                    page.
                                </p>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
